<template>
  <div class="max-w-7xl mx-auto md:mt-2">
    <template v-if="isLoading">
      <UtilPlaceholder class="w-full banner md:rounded" />

      <div class="flex items-start flex-wrap md:flex-no-wrap mt-4 mb-16">
        <UtilPlaceholder class="sidebar rounded" style="height: 647px;" />

        <div class="main-content">
          <div class="flex flex-wrap">
            <div v-for="index in 6" :key="index" class="w-full sm:w-6/12 lg:w-4/12 px-2 mb-4">
              <UtilPlaceholderHitCard />
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-else-if="isMissing" class="my-16">
      <slot name="missing">
        <UtilEmpty :icon="faUfoBeam" title="Not Found" class="rounded py-6 px-4 text-lg mx-auto">
          <template #message>
            <p class="mt-4">
              We can't find what you're looking for.
              <br />
              It's either been moved, deleted or does not exist.
            </p>

            <div class="flex flex-wrap items-center justify-center">
              <UtilButton to="/account" color="primary" class="mt-8 mx-2">
                <FontAwesomeIcon :icon="faUserChart" />
                <span>My Account</span>
              </UtilButton>

              <UtilButton to="/search?menu=" color="secondary" class="mt-8 mx-2">
                <FontAwesomeIcon :icon="faSearch" />
                <span>Search For Listings</span>
              </UtilButton>
            </div>
          </template>
        </UtilEmpty>
      </slot>
    </div>

    <template v-else-if="isContentReady">
      <div v-if="bannerFileRef || logoFileRef" class="md:rounded relative banner md:shadow mb-12 sm:mb-0">
        <UtilGradientOverlay v-if="$breakpoint.smAndUp" class="md:rounded" />

        <UtilImage :fileRef="bannerFileRef" cover showOriginal :rounded="$breakpoint.mdAndUp" class="h-full" />

        <div class="avatar-wrapper z-3 flex">
          <div class="avatar">
            <UtilImage :fileRef="logoFileRef" cover rounded showOriginal :isLazyLoaded="false" class="h-full" />
          </div>

          <div class="text-background ml-8 hidden sm:block">
            <slot name="title" />
          </div>
        </div>

        <div class="title-actions absolute items-center z-3 hidden md:flex">
          <slot name="titleActions" />
        </div>
      </div>

      <slot name="subHeader" />

      <div
        class="flex items-start flex-wrap md:flex-no-wrap mt-4 mb-16"
        :class="$slots.subHeader ? 'mt-4 sm:mt-8' : 'sm:mt-16 '"
      >
        <div class="sidebar w-full md:w-auto flex-shrink-0 mr-2 mb-8 md:mb-0">
          <LayoutPanel ref="sidebar">
            <slot name="sidebar" />
          </LayoutPanel>

          <slot name="sidebarSecondary" />
        </div>

        <div class="main-content">
          <slot name="mainContent" />
        </div>
      </div>

      <slot name="footer" />
    </template>
  </div>
</template>

<script>
import { faUfoBeam, faUserChart, faSearch } from '@fortawesome/pro-regular-svg-icons'
import UtilPlaceholder from '@/components/utils/placeholders/UtilPlaceholder'
import UtilEmpty from '@/components/utils/UtilEmpty'
import UtilButton from '@/components/utils/UtilButton'
import LayoutPanel from '@/components/layout/LayoutPanel'
import UtilImage from '@/components/utils/UtilImage'
import UtilGradientOverlay from '@/components/utils/UtilGradientOverlay'
import UtilPlaceholderHitCard from '@/components/utils/placeholders/UtilPlaceholderHitCard'

export default {
  name: 'LayoutPortal',
  components: {
    UtilPlaceholder,
    UtilEmpty,
    UtilButton,
    LayoutPanel,
    UtilImage,
    UtilGradientOverlay,
    UtilPlaceholderHitCard
  },
  props: {
    isContentReady: Boolean,
    isMissing: Boolean,
    bannerFileRef: String,
    logoFileRef: String,
    isLoading: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      faUfoBeam,
      faUserChart,
      faSearch
    }
  }
}
</script>

<style lang="scss" scoped>
.banner {
  height: 300px;
}

#app.smAndDown .banner {
  height: 200px;
}

#app.mdAndUp .sidebar {
  width: 320px;
}

.avatar-wrapper {
  @apply absolute items-center;
  left: 20px;
  bottom: -30px;
}

#app.smAndDown .avatar-wrapper {
  @apply items-start;
}

#app.xs .avatar-wrapper {
  left: 50%;
  transform: translateX(-50%);
}

.avatar {
  @apply border-4 border-background rounded-full overflow-hidden bg-gray-darker;
  width: 170px;
  height: 170px;
}

#app.smAndDown .avatar {
  height: 100px;
  width: 100px;
}

.title-actions {
  right: 20px;
  bottom: 20px;
}

.main-content {
  @apply relative;
  flex-grow: 1;
  flex-basis: 0; // IE 11 Fix
}
</style>
